import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Plus, Edit, Trash2, Star, MapPin, Image } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

interface Accommodation {
  id: number;
  name: string;
  type: string;
  location: string;
  rating: number;
  price_range: string;
  image_url: string;
  amenities: string[];
  description: string;
  contact_info: any;
  gallery_images: string[];
  is_active: boolean;
  is_featured: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface AccommodationFormData {
  name: string;
  type: string;
  location: string;
  rating: number;
  price_range: string;
  image_url: string;
  amenities: string[];
  description: string;
  contact_info: {
    phone?: string;
    email?: string;
    website?: string;
  };
  gallery_images: string[];
  is_active: boolean;
  is_featured: boolean;
}

export const AccommodationManager = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingAccommodation, setEditingAccommodation] = useState<Accommodation | null>(null);
  const [formData, setFormData] = useState<AccommodationFormData>({
    name: "",
    type: "hotel",
    location: "",
    rating: 5,
    price_range: "",
    image_url: "",
    amenities: [],
    description: "",
    contact_info: {},
    gallery_images: [],
    is_active: true,
    is_featured: false,
  });
  const [newAmenity, setNewAmenity] = useState("");
  const [uploading, setUploading] = useState(false);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: accommodations = [], isLoading } = useQuery({
    queryKey: ["accommodations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sns_accommodations')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as Accommodation[];
    },
  });

  const createAccommodation = useMutation({
    mutationFn: async (data: AccommodationFormData) => {
      const { error } = await supabase
        .from('sns_accommodations')
        .insert([data]);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accommodations"] });
      toast({
        title: "Success",
        description: "Accommodation created successfully",
      });
      resetForm();
      setShowForm(false);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create accommodation: " + error.message,
        variant: "destructive",
      });
    },
  });

  const updateAccommodation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: AccommodationFormData }) => {
      const { error } = await supabase
        .from('sns_accommodations')
        .update(data)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accommodations"] });
      toast({
        title: "Success",
        description: "Accommodation updated successfully",
      });
      resetForm();
      setShowForm(false);
      setEditingAccommodation(null);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update accommodation: " + error.message,
        variant: "destructive",
      });
    },
  });

  const deleteAccommodation = useMutation({
    mutationFn: async (id: number) => {
      const { error } = await supabase
        .from('sns_accommodations')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accommodations"] });
      toast({
        title: "Success",
        description: "Accommodation deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete accommodation: " + error.message,
        variant: "destructive",
      });
    },
  });

  const toggleFeatured = useMutation({
    mutationFn: async ({ id, is_featured }: { id: number; is_featured: boolean }) => {
      const { error } = await supabase
        .from('sns_accommodations')
        .update({ is_featured: !is_featured })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accommodations"] });
      toast({
        title: "Success",
        description: "Featured status updated",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update featured status: " + error.message,
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setFormData({
      name: "",
      type: "hotel",
      location: "",
      rating: 5,
      price_range: "",
      image_url: "",
      amenities: [],
      description: "",
      contact_info: {},
      gallery_images: [],
      is_active: true,
      is_featured: false,
    });
    setNewAmenity("");
  };

  const handleEdit = (accommodation: Accommodation) => {
    setFormData({
      name: accommodation.name,
      type: accommodation.type,
      location: accommodation.location,
      rating: accommodation.rating,
      price_range: accommodation.price_range,
      image_url: accommodation.image_url,
      amenities: accommodation.amenities || [],
      description: accommodation.description,
      contact_info: accommodation.contact_info || {},
      gallery_images: accommodation.gallery_images || [],
      is_active: accommodation.is_active,
      is_featured: accommodation.is_featured,
    });
    setEditingAccommodation(accommodation);
    setShowForm(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingAccommodation) {
      updateAccommodation.mutate({ id: editingAccommodation.id, data: formData });
    } else {
      createAccommodation.mutate(formData);
    }
  };

  const addAmenity = () => {
    if (newAmenity.trim() && !formData.amenities.includes(newAmenity.trim())) {
      setFormData(prev => ({
        ...prev,
        amenities: [...prev.amenities, newAmenity.trim()]
      }));
      setNewAmenity("");
    }
  };

  const removeAmenity = (amenity: string) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.filter(a => a !== amenity)
    }));
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;

      setUploading(true);

      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('Please upload an image file');
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('File size must be less than 5MB');
      }

      const fileExt = file.name.split('.').pop()?.toLowerCase() || '';
      const fileName = `${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('admin-uploads')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('admin-uploads')
        .getPublicUrl(fileName);

      setFormData(prev => ({ ...prev, image_url: publicUrl }));
      toast({ title: "Success", description: "Image uploaded successfully" });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || 'Failed to upload image',
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  if (isLoading) {
    return <div className="flex justify-center items-center p-8">Loading accommodations...</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Manage Accommodations</h2>
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogTrigger asChild>
            <Button 
              onClick={() => {
                resetForm();
                setEditingAccommodation(null);
              }}
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Accommodation
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingAccommodation ? "Edit Accommodation" : "Add New Accommodation"}
              </DialogTitle>
              <DialogDescription>
                Fill in the details for the accommodation listing.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <Tabs defaultValue="basic" className="w-full">
                <TabsList>
                  <TabsTrigger value="basic">Basic Info</TabsTrigger>
                  <TabsTrigger value="details">Details & Amenities</TabsTrigger>
                  <TabsTrigger value="contact">Contact & Images</TabsTrigger>
                </TabsList>
                
                <TabsContent value="basic" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="type">Type</Label>
                      <Select value={formData.type} onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hotel">Hotel</SelectItem>
                          <SelectItem value="resort">Resort</SelectItem>
                          <SelectItem value="guesthouse">Guesthouse</SelectItem>
                          <SelectItem value="villa">Villa</SelectItem>
                          <SelectItem value="apartment">Apartment</SelectItem>
                          <SelectItem value="hostel">Hostel</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="location">Location</Label>
                      <Input
                        id="location"
                        value={formData.location}
                        onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="price_range">Price Range</Label>
                      <Input
                        id="price_range"
                        value={formData.price_range}
                        onChange={(e) => setFormData(prev => ({ ...prev, price_range: e.target.value }))}
                        placeholder="$100-200/night"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="rating">Rating (1-5)</Label>
                    <Select value={formData.rating.toString()} onValueChange={(value) => setFormData(prev => ({ ...prev, rating: parseInt(value) }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 Star</SelectItem>
                        <SelectItem value="2">2 Stars</SelectItem>
                        <SelectItem value="3">3 Stars</SelectItem>
                        <SelectItem value="4">4 Stars</SelectItem>
                        <SelectItem value="5">5 Stars</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </TabsContent>
                
                <TabsContent value="details" className="space-y-4">
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      rows={4}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label>Amenities</Label>
                    <div className="flex gap-2 mb-2">
                      <Input
                        value={newAmenity}
                        onChange={(e) => setNewAmenity(e.target.value)}
                        placeholder="Add amenity"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAmenity())}
                      />
                      <Button type="button" onClick={addAmenity} variant="outline">
                        Add
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.amenities.map((amenity, index) => (
                        <Badge key={index} variant="secondary" className="cursor-pointer" onClick={() => removeAmenity(amenity)}>
                          {amenity} ×
                        </Badge>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={formData.is_active}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                      />
                      <Label>Active</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={formData.is_featured}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_featured: checked }))}
                      />
                      <Label>Featured</Label>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="contact" className="space-y-4">
                   <div>
                     <Label htmlFor="image_upload">Main Image</Label>
                     <Input
                       id="image_upload"
                       type="file"
                       accept="image/*"
                       onChange={handleImageUpload}
                       className="mb-2"
                     />
                     {formData.image_url && (
                       <img
                         src={formData.image_url}
                         alt="Preview"
                         className="w-32 h-32 object-cover rounded border"
                       />
                     )}
                   </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        value={formData.contact_info.phone || ""}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          contact_info: { ...prev.contact_info, phone: e.target.value }
                        }))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.contact_info.email || ""}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          contact_info: { ...prev.contact_info, email: e.target.value }
                        }))}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="website">Website</Label>
                    <Input
                      id="website"
                      value={formData.contact_info.website || ""}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        contact_info: { ...prev.contact_info, website: e.target.value }
                      }))}
                    />
                  </div>
                </TabsContent>
              </Tabs>
              
              <div className="flex justify-end gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </Button>
                 <Button 
                   type="submit" 
                   disabled={createAccommodation.isPending || updateAccommodation.isPending || uploading}
                 >
                   {editingAccommodation ? "Update" : "Create"} Accommodation
                 </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {accommodations.map((accommodation) => (
          <Card key={accommodation.id} className="hover:shadow-lg transition-shadow">
            <div className="relative">
              <img
                src={accommodation.image_url}
                alt={accommodation.name}
                className="w-full h-48 object-cover rounded-t-lg"
              />
              {accommodation.is_featured && (
                <Badge className="absolute top-2 left-2 bg-yellow-500">
                  Featured
                </Badge>
              )}
              <div className="absolute top-2 right-2 bg-black/50 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1">
                <Star className="w-3 h-3 text-yellow-400 fill-current" />
                <span className="text-white text-xs">{accommodation.rating}</span>
              </div>
            </div>
            
            <CardHeader>
              <CardTitle className="flex items-start justify-between">
                <span>{accommodation.name}</span>
                <Badge variant="outline" className="text-xs">
                  {accommodation.type}
                </Badge>
              </CardTitle>
              <CardDescription className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {accommodation.location}
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                {accommodation.description}
              </p>
              
              <div className="flex flex-wrap gap-1 mb-3">
                {accommodation.amenities?.slice(0, 3).map((amenity, index) => (
                  <Badge key={index} variant="secondary" className="text-xs">
                    {amenity}
                  </Badge>
                ))}
                {accommodation.amenities?.length > 3 && (
                  <Badge variant="secondary" className="text-xs">
                    +{accommodation.amenities.length - 3} more
                  </Badge>
                )}
              </div>
              
              <div className="text-lg font-semibold text-primary mb-4">
                {accommodation.price_range}
              </div>
              
              <div className="flex justify-between items-center">
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(accommodation)}
                  >
                    <Edit className="w-3 h-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toggleFeatured.mutate({ 
                      id: accommodation.id, 
                      is_featured: accommodation.is_featured 
                    })}
                    disabled={toggleFeatured.isPending}
                  >
                    <Star className="w-3 h-3" />
                  </Button>
                   <Button
                     size="sm"
                     variant="destructive"
                     onClick={() => {
                       if (window.confirm('Are you sure you want to delete this accommodation?')) {
                         deleteAccommodation.mutate(accommodation.id);
                       }
                     }}
                     disabled={deleteAccommodation.isPending}
                   >
                     <Trash2 className="w-3 h-3" />
                   </Button>
                </div>
                <Badge 
                  variant={accommodation.is_active ? "default" : "secondary"}
                  className="text-xs"
                >
                  {accommodation.is_active ? "Active" : "Inactive"}
                </Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {accommodations.length === 0 && (
        <div className="text-center py-12">
          <Image className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-muted-foreground mb-2">
            No accommodations yet
          </h3>
          <p className="text-sm text-muted-foreground">
            Add your first accommodation to get started.
          </p>
        </div>
      )}
    </div>
  );
};

export default AccommodationManager;